import { useEffect, useState } from 'react';
import { ConfigProvider, Tabs, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@hooks/redux';
import { getTransactionListApi } from '@http/transactions';
import TransactionList from '@components/Transactions/TransactionList';
import TransactionFilters from '@components/Transactions/TransactionFilters';
import { STATUS_OPTIONS, cleanFilters, isSameFilters } from '@components/Transactions/constants';
import { TransactionFilterValues } from '@components/Transactions/types';

const ACTIVE_COLOR = '#011957';

const TABS_THEME = {
  components: {
    Tabs: {
      itemColor: ACTIVE_COLOR,
      itemHoverColor: ACTIVE_COLOR,
      itemActiveColor: ACTIVE_COLOR,
      itemSelectedColor: ACTIVE_COLOR,
    },
  },
};

interface TransactionQuery {
  status: string;
  filters: TransactionFilterValues;
  page: number;
}

function Index() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const transactions = useAppSelector(state => state.transactionsReducer.transactionsHistory);

  const [query, setQuery] = useState<TransactionQuery>({ status: '', filters: {}, page: 1 });

  useEffect(() => {
    dispatch(getTransactionListApi(query.page, { ...query.filters, status: query.status }));
  }, [query]);

  useEffect(() => {
    const totalPages = transactions?.total_pages ?? 0;
    if (query.page > 1 && query.page > totalPages) {
      setQuery(prev => ({ ...prev, page: Math.max(totalPages, 1) }));
    }
  }, [transactions]);

  const reloadCurrentPage = () => {
    setQuery(prev => ({ ...prev }));
  };

  const changeStatus = (status = '') => {
    setQuery(prev => ({ ...prev, status, page: 1 }));
  };

  const changePage = (page: number) => {
    setQuery(prev => ({ ...prev, page }));
  };

  const applyFilters = (filters: TransactionFilterValues) => {
    const cleaned = cleanFilters(filters);
    setQuery(prev => (isSameFilters(prev.filters, cleaned) ? prev : { ...prev, filters: cleaned, page: 1 }));
  };

  const statusOptions = STATUS_OPTIONS.map(option => ({ value: option.value, label: t(option.label) }));

  return (
    <div className='flex justify-center'>
      <div className='lg:w-[1130px] w-full max-lg:px-4'>
        <div className='py-4 lg:py-8 w-full h-full bg-white mb-12'>
          <h1 className='max-md:hidden text-xl mb-2 font-medium color-active'>{t('Transactions')}</h1>
          <div className='flex justify-between md:hidden'>
            <h1 className='text-xl font-medium color-active'>{t('Transactions')}</h1>
            <Select
              style={{ width: 210 }}
              allowClear
              placeholder={t('Status')}
              value={query.status}
              options={statusOptions}
              onChange={changeStatus}
            />
          </div>
          <TransactionFilters onApply={applyFilters} />
          <ConfigProvider theme={TABS_THEME}>
            <Tabs
              className='max-md:hidden transactions-tabs'
              activeKey={query.status}
              items={statusOptions.map(option => ({ key: option.value, label: option.label }))}
              onChange={changeStatus}
            />
          </ConfigProvider>
          <TransactionList
            data={transactions}
            page={query.page}
            status={query.status}
            onPageChange={changePage}
            onReload={reloadCurrentPage}
          />
        </div>
      </div>
    </div>
  );
}

export default Index;
